import React, { useState } from 'react';
import {
  TrophyIcon,
  ZapIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SkullIcon,
  CrownIcon,
  GlobeIcon,
  SparklesIcon,
  ClockIcon,
  CoinsIcon,
  CopyIcon,
  CheckIcon,
  MaximizeIcon,
  XIcon,
  GitBranchIcon,
  StarIcon,
  TargetIcon
} from 'lucide-react';
import { resolveFriendlyName } from '../shared/modelMap';
import EvolutionView from './EvolutionView';

const ParallelUniversesResults = ({ multiverseData }) => {
  const [expandedUniverse, setExpandedUniverse] = useState(null);
  const [copiedContent, setCopiedContent] = useState(null);
  const [selectedUniverse, setSelectedUniverse] = useState(null);

  if (!multiverseData || !multiverseData.success) {
    return null;
  }

  const {
    multiverseWinner,
    winCounts,
    avgScores,
    dominanceLevel,
    isTie,
    tieBreaker,
    insight,
    initialBattle,
    universes,
    usage
  } = multiverseData;

  const getModelColor = (model) => {
    const key = model?.toLowerCase();
    if (key?.includes('gpt')) return 'emerald';
    if (key?.includes('claude')) return 'blue';
    if (key?.includes('gemini')) return 'purple';
    return 'slate';
  };

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
      blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
      purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
      slate: 'from-slate-500/20 to-slate-600/10 border-slate-500/30 text-slate-400'
    };
    return colors[color] || colors.slate;
  };

  const getBgColor = (color) => {
    const colors = {
      emerald: 'bg-emerald-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      slate: 'bg-slate-500'
    };
    return colors[color] || colors.slate;
  };

  const getDominanceColor = () => {
    if (dominanceLevel === 'absolute') return 'from-yellow-500 via-orange-500 to-red-500';
    if (dominanceLevel === 'strong') return 'from-green-500 via-emerald-500 to-teal-500';
    if (dominanceLevel === 'narrow') return 'from-blue-500 via-indigo-500 to-purple-500';
    return 'from-slate-500 via-slate-600 to-slate-700';
  };

  const getDominanceLabel = () => {
    if (dominanceLevel === 'absolute') return 'ABSOLUTE DOMINANCE';
    if (dominanceLevel === 'strong') return 'STRONG WINNER';
    if (dominanceLevel === 'narrow') return 'NARROW VICTORY';
    return 'CONTESTED';
  };

  const handleCopy = async (content, id) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedContent(id);
      setTimeout(() => setCopiedContent(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Multiverse Winner Banner */}
      <div className={`relative overflow-hidden glass border rounded-2xl p-6 shadow-2xl bg-gradient-to-r ${getDominanceColor()}`}>
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0 overflow-hidden">
          {/* Animated stars/particles */}
          <div className="absolute top-4 left-10 w-2 h-2 bg-white/50 rounded-full animate-ping"></div>
          <div className="absolute top-12 right-20 w-1 h-1 bg-white/30 rounded-full animate-pulse"></div>
          <div className="absolute bottom-8 left-1/4 w-1.5 h-1.5 bg-white/40 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-white/20 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center justify-center gap-4 mb-4">
            <GlobeIcon className="h-10 w-10 text-white animate-spin" style={{ animationDuration: '10s' }} />
            <h2 className="text-3xl font-black text-white">Multiverse Analysis Complete</h2>
            <GlobeIcon className="h-10 w-10 text-white animate-spin" style={{ animationDuration: '10s', animationDirection: 'reverse' }} />
          </div>

          <div className="text-center mb-4">
            <span className={`inline-block px-4 py-1 rounded-full text-xs font-bold tracking-wider ${
              dominanceLevel === 'absolute' ? 'bg-yellow-500/30 text-yellow-200' :
              dominanceLevel === 'strong' ? 'bg-green-500/30 text-green-200' :
              dominanceLevel === 'narrow' ? 'bg-blue-500/30 text-blue-200' :
              'bg-slate-500/30 text-slate-200'
            }`}>
              {getDominanceLabel()}
            </span>
          </div>

          <div className="text-center">
            <p className="text-lg text-white/80 mb-3">Across 3 parallel elimination paths</p>
            <div className="inline-flex items-center gap-3 px-6 py-3 glass rounded-xl border border-white/30">
              <CrownIcon className="h-8 w-8 text-yellow-400" />
              <span className="text-2xl font-black text-white">
                {resolveFriendlyName(multiverseWinner?.toLowerCase())} is the TRUE Winner!
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Win Distribution Visualization */}
      <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <TargetIcon className="h-6 w-6 text-cyan-400" />
          <h3 className="text-xl font-bold text-white">Universe Win Distribution</h3>
        </div>

        {/* Visual Win Bars */}
        <div className="space-y-4 mb-6">
          {['gpt', 'claude', 'gemini'].map(model => {
            const wins = winCounts[model];
            const isWinner = model === multiverseWinner?.toLowerCase();
            const color = getModelColor(model);
            const percentage = (wins / 3) * 100;

            return (
              <div key={model} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`font-bold ${isWinner ? 'text-yellow-400' : 'text-white'}`}>
                      {resolveFriendlyName(model)}
                    </span>
                    {isWinner && <CrownIcon className="h-4 w-4 text-yellow-400" />}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 text-sm">Avg Score: {avgScores[model]}</span>
                    <span className={`font-bold text-lg ${isWinner ? 'text-yellow-400' : 'text-white'}`}>
                      {wins}/3 wins
                    </span>
                  </div>
                </div>
                <div className="h-8 bg-slate-800 rounded-lg overflow-hidden">
                  <div
                    className={`h-full transition-all duration-1000 ease-out ${getBgColor(color)} ${isWinner ? 'animate-pulse' : ''}`}
                    style={{ width: `${percentage}%` }}
                  >
                    {wins > 0 && (
                      <div className="h-full flex items-center justify-end pr-3">
                        {Array.from({ length: wins }).map((_, i) => (
                          <TrophyIcon key={i} className="h-4 w-4 text-white/80 mr-1" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Insight Box */}
        <div className={`p-4 rounded-xl border ${
          dominanceLevel === 'absolute' ? 'bg-yellow-500/10 border-yellow-500/30' :
          dominanceLevel === 'strong' ? 'bg-green-500/10 border-green-500/30' :
          'bg-slate-800/50 border-slate-700/30'
        }`}>
          <div className="flex items-start gap-3">
            <SparklesIcon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
              dominanceLevel === 'absolute' ? 'text-yellow-400' :
              dominanceLevel === 'strong' ? 'text-green-400' :
              'text-cyan-400'
            }`} />
            <div>
              <p className="text-white font-medium">{insight}</p>
              {isTie && tieBreaker && (
                <p className="text-slate-400 text-sm mt-1">
                  Tie-breaker: Highest average final score across winning universes
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Evolution View - Interactive Score Progression */}
      {universes && universes.length > 0 && initialBattle && (
        <EvolutionView
          universes={universes}
          initialBattle={initialBattle}
          multiverseWinner={multiverseWinner}
          mode="multiverse"
        />
      )}

      {/* Universe Cards - Timeline View */}
      <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <GitBranchIcon className="h-6 w-6 text-purple-400" />
          <h3 className="text-xl font-bold text-white">Parallel Timelines</h3>
          <span className="text-slate-400 text-sm ml-auto">Click to expand</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {universes.map((universe, idx) => {
            const isExpanded = expandedUniverse === idx;
            const universeWinnerColor = getModelColor(universe.winner);
            const isMultiverseWinner = universe.winner?.toLowerCase() === multiverseWinner?.toLowerCase();

            return (
              <div
                key={idx}
                className={`glass rounded-xl border transition-all cursor-pointer ${
                  isMultiverseWinner ? 'border-yellow-500/50 ring-1 ring-yellow-500/30' : 'border-slate-700/50'
                } ${isExpanded ? 'lg:col-span-3' : ''}`}
              >
                {/* Universe Header */}
                <button
                  onClick={() => setExpandedUniverse(isExpanded ? null : idx)}
                  className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-all rounded-t-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${
                      idx === 0 ? 'from-red-500 to-orange-500' :
                      idx === 1 ? 'from-blue-500 to-cyan-500' :
                      'from-purple-500 to-pink-500'
                    }`}>
                      <GlobeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-white">{universe.name}</p>
                      <p className="text-xs text-slate-400">
                        {resolveFriendlyName(universe.firstEliminated)} eliminated first
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                      isMultiverseWinner ? 'bg-yellow-500/20' : 'bg-slate-700/50'
                    }`}>
                      {isMultiverseWinner && <StarIcon className="h-4 w-4 text-yellow-400" />}
                      <CrownIcon className={`h-4 w-4 ${isMultiverseWinner ? 'text-yellow-400' : 'text-slate-400'}`} />
                      <span className={`font-medium ${isMultiverseWinner ? 'text-yellow-300' : 'text-slate-300'}`}>
                        {universe.winner}
                      </span>
                    </div>
                    <span className="text-slate-400 text-sm">{universe.finalScore} pts</span>
                    {isExpanded ? (
                      <ChevronUpIcon className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Elimination Path Mini View (when collapsed) */}
                {!isExpanded && (
                  <div className="px-4 pb-4">
                    <div className="flex items-center justify-center gap-2 text-xs">
                      <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded">
                        {resolveFriendlyName(universe.firstEliminated)}
                      </span>
                      <span className="text-slate-600">→</span>
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded">
                        {resolveFriendlyName(universe.secondEliminated)}
                      </span>
                      <span className="text-slate-600">→</span>
                      <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded flex items-center gap-1">
                        <CrownIcon className="h-3 w-3" />
                        {resolveFriendlyName(universe.winner?.toLowerCase())}
                      </span>
                    </div>
                  </div>
                )}

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-4 border-t border-slate-700/50 space-y-4">
                    {/* Elimination Timeline */}
                    <div className="flex items-center justify-center gap-4 py-4">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-red-500/20 border-2 border-red-500/50 flex items-center justify-center mb-2">
                          <SkullIcon className="h-8 w-8 text-red-400" />
                        </div>
                        <span className="text-red-300 font-medium text-sm">1st Out</span>
                        <span className="text-white font-bold">{resolveFriendlyName(universe.firstEliminated)}</span>
                      </div>
                      <div className="text-slate-600 text-2xl">→</div>
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-orange-500/20 border-2 border-orange-500/50 flex items-center justify-center mb-2">
                          <SkullIcon className="h-8 w-8 text-orange-400" />
                        </div>
                        <span className="text-orange-300 font-medium text-sm">2nd Out</span>
                        <span className="text-white font-bold">{resolveFriendlyName(universe.secondEliminated)}</span>
                      </div>
                      <div className="text-slate-600 text-2xl">→</div>
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-yellow-500/20 border-2 border-yellow-500/50 flex items-center justify-center mb-2 animate-pulse">
                          <CrownIcon className="h-8 w-8 text-yellow-400" />
                        </div>
                        <span className="text-yellow-300 font-medium text-sm">Champion</span>
                        <span className="text-white font-bold">{resolveFriendlyName(universe.winner?.toLowerCase())}</span>
                      </div>
                    </div>

                    {/* Final Response */}
                    {universe.finalResponse && universe.finalResponse.success && (
                      <div className={`glass rounded-xl p-4 border ${getColorClasses(universeWinnerColor)}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-slate-300">
                            Final Response from {resolveFriendlyName(universe.winner?.toLowerCase())}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleCopy(universe.finalResponse.content, `universe-${idx}`)}
                              className="p-1.5 glass rounded-lg hover:bg-white/10 transition-all"
                            >
                              {copiedContent === `universe-${idx}` ? (
                                <CheckIcon className="h-4 w-4 text-green-400" />
                              ) : (
                                <CopyIcon className="h-4 w-4 text-slate-400" />
                              )}
                            </button>
                            <button
                              onClick={() => setSelectedUniverse(idx)}
                              className="p-1.5 glass rounded-lg hover:bg-white/10 transition-all"
                            >
                              <MaximizeIcon className="h-4 w-4 text-slate-400" />
                            </button>
                          </div>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3 max-h-40 overflow-y-auto">
                          <p className="text-slate-200 text-sm whitespace-pre-wrap line-clamp-6">
                            {universe.finalResponse.content}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <ClockIcon className="h-3 w-3" />
                            {(universe.finalResponse.responseTime / 1000).toFixed(2)}s
                          </span>
                          <span className="flex items-center gap-1">
                            <CoinsIcon className="h-3 w-3" />
                            {universe.finalResponse.tokens} tokens
                          </span>
                          <span className="ml-auto font-medium text-yellow-400">
                            Score: {universe.finalScore}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Round Details */}
                    <div className="space-y-2">
                      <p className="text-sm text-slate-400 font-medium">Round Scores</p>
                      {universe.rounds.map((round, rIdx) => (
                        <div key={rIdx} className="flex items-center gap-4 text-sm">
                          <span className="w-20 text-slate-500">Round {round.round}:</span>
                          {['gpt', 'claude', 'gemini'].map(m => {
                            const score = round.scores[m];
                            const isElim = round.eliminated === m;
                            return (
                              <span key={m} className={`flex items-center gap-1 ${
                                score === 0 ? 'text-slate-600' :
                                isElim ? 'text-red-400' : 'text-slate-300'
                              }`}>
                                {resolveFriendlyName(m).split(' ')[0]}:
                                {score > 0 ? Math.round(score) : 'DQ'}
                                {isElim && <SkullIcon className="h-3 w-3" />}
                              </span>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Initial Battle Summary */}
      <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <ZapIcon className="h-6 w-6 text-cyan-400" />
          <h3 className="text-xl font-bold text-white">Initial Battle (Shared Starting Point)</h3>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {['gpt', 'claude', 'gemini'].map(model => {
            const score = initialBattle.scores[model];
            const color = getModelColor(model);

            return (
              <div key={model} className={`glass rounded-xl p-4 border ${getColorClasses(color)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">{resolveFriendlyName(model)}</span>
                </div>
                <p className="text-3xl font-bold text-white">{Math.round(score)}</p>
                <p className="text-xs text-slate-400 mt-1">Initial Score</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Usage Info */}
      {usage && (
        <div className="glass border border-slate-700/50 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-400">
            <CoinsIcon className="h-5 w-5" />
            <span>Parallel Universes used 7 prompts (1 initial + 2 per universe)</span>
          </div>
          <div className="text-slate-300">
            <span className="font-bold text-cyan-400">{usage.remaining}</span> prompts remaining
          </div>
        </div>
      )}

      {/* Expanded Response Modal */}
      {selectedUniverse !== null && universes[selectedUniverse]?.finalResponse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-4xl max-h-[90vh] glass border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
              <div className="flex items-center gap-3">
                <GlobeIcon className="h-6 w-6 text-purple-400" />
                <h3 className="text-lg font-bold text-white">
                  {universes[selectedUniverse].name} - Final Response from {resolveFriendlyName(universes[selectedUniverse].winner?.toLowerCase())}
                </h3>
              </div>
              <button
                onClick={() => setSelectedUniverse(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-all"
              >
                <XIcon className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <pre className="text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">
                {universes[selectedUniverse].finalResponse.content}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParallelUniversesResults;
